import React from 'react';
import { useBreathingRhythm, BreathingConfig, Phase } from '../../hooks/useBreathingRhythm';
import { useBreathingSound } from '../../hooks/useBreathingSound';
import { FaVolumeMute, FaVolumeUp } from 'react-icons/fa'; // Assuming react-icons available, otherwise text
import './BreathingExerciseSloped.css';

interface BreathingExerciseSlopedProps {
    config: BreathingConfig;
    onComplete: () => void;
    onCancel: () => void;
}

const BreathingExerciseSloped: React.FC<BreathingExerciseSlopedProps> = ({ config, onComplete, onCancel }) => {
    const { phase, cycleCount, isActive, handleStart, handleStop, getDuration } = useBreathingRhythm(config, onComplete);
    const { isMuted, toggleMute } = useBreathingSound(phase, getDuration(phase), isActive);

    // We need to sync the background scrolling speed with the total cycle duration.
    const totalCycleDuration = config.inhaleDuration + config.exhaleDuration + (config.holdDuration || 0) + (config.holdAfterExhale || 0);

    // Calculate SVG path points
    // Assume width 300px per cycle for the visual segment
    const segmentWidth = 600;
    const height = 150; // Amplitude
    const bassline = 200; // Y position of bottom
    const peak = bassline - height;

    // Ratio of inhale vs total
    const inhaleRatio = config.inhaleDuration / totalCycleDuration;
    const peakX = segmentWidth * inhaleRatio;

    // Path: Start(0, bassline) -> Peak(peakX, peak) -> End(segmentWidth, bassline)
    const pathD = `M 0,${bassline} L ${peakX},${peak} L ${segmentWidth},${bassline}`;

    // Derived state for UI
    const getInstruction = (p: Phase) => {
        switch (p) {
            case 'inhale': return 'Вдох носом';
            case 'hold': return 'Задержите';
            case 'exhale': return 'Выдох ртом';
            case 'holdAfter': return 'Задержите';
            default: return 'Нажмите начать';
        }
    };

    // Ball Animation Logic
    const getBallStyle = () => {
        // We only animate Y. X is fixed at 50% of container.

        let top = `${bassline}px`; // Default bottom
        let transition = 'none';

        const duration = getDuration(phase);

        if (phase === 'inhale') {
            top = `${peak}px`;
            transition = `top ${duration}s linear`;
        } else if (phase === 'exhale') {
            top = `${bassline}px`;
            transition = `top ${duration}s linear`;
        } else if (phase === 'hold') {
            top = `${peak}px`;
            // Stay there
        } else if (phase === 'idle') {
            top = `${bassline}px`;
            transition = 'top 0.5s ease-out';
        }

        return {
            top,
            transition
        };
    };

    return (
        <div className="breathing-sloped-container">
            <div className={`sloped-visual`}>

                {/*
                  The track needs to be wider than the container and repeated.
                  If container is 600px, 3 segments of 600px = 1800px.
                  We animate from 0 to -600px (one full cycle) over totalCycleDuration.
                  Linearly infinite.
                */}
                <div
                    className="scrolling-track"
                    style={{
                        width: `${segmentWidth * 3}px`,
                        left: '50%',
                        marginLeft: `-${segmentWidth}px`,
                        transform: isActive ? 'translateX(-33.333%)' : 'translateX(0)', // Move 1 cycle?
                        // Actually we want keyframe animation.
                        transition: isActive ? 'none' : 'transform 0.5s ease-out', // Reset smoothness
                        animation: isActive ? `scrollTrack ${totalCycleDuration}s linear infinite` : 'none',
                    }}
                >
                    {/* Render 3 segments to ensure infinite scroll illusion */}
                    {[0, 1, 2].map(i => (
                        <div key={i} className="track-segment" style={{ width: `${segmentWidth}px` }}>
                            <svg width="100%" height="100%">
                                <path d={pathD} className="slope-line" />
                                {/* Add fill gradient under the line for better look? Optional */}
                                <path d={`${pathD} L ${segmentWidth},250 L 0,250 Z`} fill="rgba(116, 235, 213, 0.1)" stroke="none" />
                            </svg>
                        </div>
                    ))}
                </div>

                <div
                    className="slope-ball"
                    style={getBallStyle()}
                />

                {/* Sound Control Component */}
                <button
                    className="sound-toggle-button"
                    onClick={toggleMute}
                    aria-label={isMuted ? "Unmute" : "Mute"}
                >
                    {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                </button>
            </div>

            <div className="sloped-instruction">
                {isActive ? getInstruction(phase) : 'Нажмите начать'}
            </div>

            <div className="breathing-controls">
                {!isActive ? (
                    <>
                        <button className="breathing-button secondary" onClick={onCancel}>
                            Назад
                        </button>
                        <button className="breathing-button" onClick={handleStart}>
                            Начать
                        </button>
                    </>
                ) : (
                    <button className="breathing-button secondary" onClick={handleStop}>
                        Стоп
                    </button>
                )}
            </div>

            {
                isActive && (
                    <div style={{ marginTop: '20px', color: '#999', fontSize: '0.9rem' }}>
                        Цикл: {cycleCount + 1} / {config.cycles}
                    </div>
                )
            }
        </div >
    );
};

export default BreathingExerciseSloped;
